import { useState, useCallback } from 'react';
import { UsersDetail } from '../../../types/settings/UsersTypes';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import ModernUserForm from '../../../components/Forms/ModernUserForm';
import { getUsers } from '@/actions/data-fetch-users';
import { toastMessage } from '@/utils/toast-message';
import ModernUsersTable from './ModernUsersTable';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { PaginationProps, UsersApiData } from '../../../types/settings/settings';
import { Users, Trash2 } from 'lucide-react';
import { optimizedApi } from '@/utils/api/optimized-api';

export function UsersClientWrapper({
    initialData,
    pagination
}: UsersApiData) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UsersDetail | null>(null);
    const [data, setData] = useState<UsersDetail[]>(initialData || []);
    const [currentPagination, setCurrentPagination] = useState<PaginationProps | undefined>(pagination);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [isPurgingCache, setIsPurgingCache] = useState(false);

    const fetchData = useCallback(async (params: {
        page: number,
        search_term?: string,
        sort_column?: string,
        sort_order?: 'ASC' | 'DESC'
    }) => {
        try {
            setIsLoading(true);
            const response = await getUsers(params);
            if (response.data) {
                setData(response.data.users || []);
                setCurrentPagination(response.data.pagination);
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                toastMessage(error.message, "error");
            } else {
                toastMessage("An unknown error occurred", "error");
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleOpenModal = (user?: UsersDetail) => {
        setSelectedUser(user || null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const handleSuccess = async (updatedUser: UsersDetail) => {
        // Optimistic update of local state
        setData(prevData => {
            // If user exists, update; otherwise, add new user
            const userIndex = prevData.findIndex(u => u.uuid === updatedUser.uuid);
            if (userIndex !== -1) {
                // Update existing user
                const newData = [...prevData];
                newData[userIndex] = updatedUser;
                return newData;
            } else {
                // Add new user
                return [...prevData, updatedUser];
            }
        });

        // Refresh data from server
        await fetchData({
            page: currentPagination?.currentPage || 1,
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder
        });

        // Close modal only for new user creation, keep open for updates
        if (!selectedUser) {
            handleCloseModal();
        }

        // Show success message
        toastMessage(
            selectedUser ? 'User updated successfully' : 'User created successfully',
            'success'
        );
    };

    const handlePageChange = async (page: number) => {
        await fetchData({
            page,
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };

    const handleSearch = async (term: string): Promise<void> => {
        setSearchTerm(term);
        await fetchData({
            page: 1,
            search_term: term,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };

    const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
        setSortColumn(column);
        setSortOrder(order);
        await fetchData({
            page: currentPagination?.currentPage || 1,
            search_term: searchTerm,
            sort_column: column,
            sort_order: order
        });
    };

    const handlePurgeCache = async () => {
        try {
            setIsPurgingCache(true);
            const response = await optimizedApi.delete('/users/cache/purge');
            if (response.success) {
                toastMessage('Users cache purged successfully', 'success');
                // Refresh data after purging cache
                await fetchData({
                    page: 1,
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortOrder
                });
            } else {
                toastMessage(response.message || 'Failed to purge cache', 'error');
            }
        } catch (error) {
            console.error('Error purging cache:', error);
            toastMessage('Failed to purge users cache', 'error');
        } finally {
            setIsPurgingCache(false);
        }
    };

    return (
        <>
            <ModernSettingHeader
                title="Team Members"
                description="Manage your team members, their roles, and notification preferences. Click on any user name to edit their details."
                buttonText="Add New User"
                onClick={() => handleOpenModal()}
                icon={<Users className="w-6 h-6" />}
                secondaryButton={{
                    text: "Purge Users Cache",
                    onClick: handlePurgeCache,
                    icon: <Trash2 className="w-4 h-4" />,
                    variant: "warning",
                    loading: isPurgingCache
                }}
            />
            <div className="p-6">
                <ModernUsersTable
                    initialData={data}
                    onEdit={handleOpenModal}
                    pagination={currentPagination}
                    onPageChange={handlePageChange}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                />
            </div>

            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={selectedUser ? 'Edit User' : 'Add New User'}
                subtitle={selectedUser ? 'Update user details and permissions' : 'Create a new user account and set their permissions'}
                icon={<Users className="w-6 h-6" />}
            >
                <ModernUserForm
                    onClose={handleCloseModal}
                    uuid={selectedUser?.uuid}
                    initialData={selectedUser}
                    onSuccess={handleSuccess}
                />
            </SettingsSlideModal>
        </>
    );
}